import {
  ChartBarIcon,
  ClipboardDocumentListIcon,
  HeartIcon,
  BookOpenIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

export type HealthSectionId = 'overview' | 'records' | 'care' | 'reference' | 'reports';
export type HealthRecordsView = 'tests' | 'vaccinations' | 'treatments';
export type HealthCareView = 'investigation' | 'workflows' | 'schedules' | 'checklists' | 'assessments';
export type HealthReferenceView = 'diseases' | 'groups' | 'medicines';

export interface HealthSectionView {
  id: string;
  name: string;
}

export interface HealthSection {
  id: HealthSectionId;
  name: string;
  icon: typeof ChartBarIcon;
  description: string;
  views?: HealthSectionView[];
}

export const HEALTH_SECTIONS: HealthSection[] = [
  {
    id: 'overview',
    name: 'Overview',
    icon: ChartBarIcon,
    description: 'Flock health snapshot, alerts, and quick actions',
  },
  {
    id: 'records',
    name: 'Records',
    icon: ClipboardDocumentListIcon,
    description: 'Tests, vaccinations, and treatments',
    views: [
      { id: 'tests', name: 'Health Tests' },
      { id: 'vaccinations', name: 'Vaccinations' },
      { id: 'treatments', name: 'Treatments' },
    ],
  },
  {
    id: 'care',
    name: 'Care Plans',
    icon: HeartIcon,
    description: 'Workflows, checkups, reminders, and assessments',
    views: [
      { id: 'investigation', name: 'Symptom investigation' },
      { id: 'workflows', name: 'Active workflows' },
      { id: 'schedules', name: 'Checkups & reminders' },
      { id: 'checklists', name: 'Checklists' },
      { id: 'assessments', name: 'Assessments' },
    ],
  },
  {
    id: 'reference',
    name: 'Reference',
    icon: BookOpenIcon,
    description: 'Diseases, groups, and medicines catalog',
    views: [
      { id: 'diseases', name: 'Diseases' },
      { id: 'groups', name: 'Disease Groups' },
      { id: 'medicines', name: 'Medicines' },
    ],
  },
  {
    id: 'reports',
    name: 'Reports',
    icon: DocumentTextIcon,
    description: 'Health reports and analytics',
  },
];

export function parseHealthSearchParams(search: URLSearchParams) {
  const section = (search.get('section') as HealthSectionId) || 'overview';
  const view = search.get('view') || '';
  const birdId = search.get('birdId') || '';

  const validSection = HEALTH_SECTIONS.some((s) => s.id === section) ? section : 'overview';
  const sectionDef = HEALTH_SECTIONS.find((s) => s.id === validSection);
  let normalizedView = view === 'checkups' ? 'schedules' : view;
  const defaultView = sectionDef?.views?.[0]?.id || '';
  const validView =
    sectionDef?.views?.some((v) => v.id === normalizedView) ? normalizedView : defaultView;

  return { section: validSection, view: validView, birdId };
}

export function buildHealthSearchParams(
  section: HealthSectionId,
  view?: string,
  birdId?: string,
): string {
  const params = new URLSearchParams();
  if (section !== 'overview') params.set('section', section);
  if (view) params.set('view', view);
  if (birdId) params.set('birdId', birdId);
  const query = params.toString();
  return query ? `?${query}` : '';
}
